/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";

interface ConfirmActionProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  actionLabel: string;
  icon?: LucideIcon;
  iconColor?: string;
  onConfirm: () => Promise<void>;
  withTextConfirmation?: boolean;
  textToConfirm?: string;
}

export function ConfirmActionPopup({
  open,
  onOpenChange,
  title,
  description,
  actionLabel,
  icon: Icon,
  iconColor = "text-red-600",
  onConfirm,
  withTextConfirmation = false,
  textToConfirm = "confirm",
}: ConfirmActionProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [text, setText] = useState("");
  const handleAction = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || "Action failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle
            className={cn(
              Icon && "flex items-center justify-center sm:justify-normal gap-2"
            )}
          >
            {Icon && <Icon className={`h-5 w-5 ${iconColor}`} />}
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {withTextConfirmation && (
          <div className="flex flex-col gap-2">
            <span className="text-sm text-muted-foreground">
              Please enter the text{" "}
              <span className="font-bold select-none">{textToConfirm}</span> to
              confirm the action.
            </span>
            <Input
              type="text"
              placeholder="Enter text to confirm"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleAction}
            disabled={
              isLoading || (withTextConfirmation && text !== textToConfirm)
            }
          >
            {isLoading ? "Processing..." : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
